// src/pages/Exams/generate/CompareFormsModal.tsx
import { useMemo } from 'react';
import type { QuestionDraft } from './types';
import { Modal } from '../../../school_panel/components/ui';

interface Props {
  open: boolean;
  onClose: () => void;
  questions: QuestionDraft[];
  chapterNames?: Record<number, string>;
}

// First 120 chars of the plain-text stem, tags stripped, so forms can be
// scanned side-by-side without the full question body (spec §3.4, §7.6).
function stemOf(q: QuestionDraft): string {
  return q.question.replace(/<[^>]+>/g, '').slice(0, 120);
}

export function CompareFormsModal({ open, onClose, questions, chapterNames }: Props) {
  // Group by form_label, then by chapter_id within each form.
  const byForm = useMemo(() => {
    const forms = new Map<string, Map<number, QuestionDraft[]>>();
    for (const q of questions) {
      const label = q.form_label || 'A';
      if (!forms.has(label)) forms.set(label, new Map());
      const byChapter = forms.get(label)!;
      const list = byChapter.get(q.chapter_id) ?? [];
      list.push(q);
      byChapter.set(q.chapter_id, list);
    }
    return forms;
  }, [questions]);

  if (!open) return null;

  const formLabels = Array.from(byForm.keys()).sort();

  return (
    <Modal isOpen={open} onClose={onClose} title="Compare forms" size="xl">
      {/* The Modal body scrolls, so this grid just lays the forms side by side. */}
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        {formLabels.map(label => {
          const byChapter = byForm.get(label)!;
          const chapterIds = Array.from(byChapter.keys()).sort((a, b) => a - b);
          return (
            <div key={label} className="min-w-0">
              <h4 className="mb-2 font-display font-bold text-ink">Form {label}</h4>
              {chapterIds.map(cid => (
                <div key={cid} className="mb-3">
                  <h5 className="mb-1 text-xs font-semibold text-muted">
                    {chapterNames?.[cid] ?? `Chapter ${cid}`}
                  </h5>
                  <ul className="space-y-1 text-xs text-ink">
                    {byChapter.get(cid)!.map((q, i) => (
                      <li key={i} className="rounded-[10px] border border-line bg-surface-2 p-1.5">
                        {stemOf(q)}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          );
        })}
      </div>
    </Modal>
  );
}
